/**
 * Option Indicator page — redesigned to match the standalone "OI
 * Dashboard" app's dense, professional financial-terminal layout, per
 * three explicit scoping decisions (asked and confirmed):
 *
 * 1. Frontend-only. No new backend endpoints/computation were added.
 *    Every value below comes from fields `OptionIndicatorDashboardRead`/
 *    `OIScoreSnapshot` already carry — most were already being fetched
 *    by earlier versions of this page but never rendered. Sections the
 *    OI Dashboard reference shows with NO backing data today
 *    (timeframe-bucketed OI flow, strike velocity, OI-vs-price
 *    divergence, ATM CE/PE ratio, zone skew, new-strike activation, roll
 *    detection, per-strike fresh-writing/unwinding tables, multi-snapshot
 *    strike migration, a 30-min PCR-slope projection, raw ATM IV%,
 *    expected daily move) are disclosed explicitly in the "Not Yet
 *    Available" panel rather than fabricated — same convention already
 *    used in `OIChartsPage.tsx`. Time-series charts (PCR+VWOI, Net Ratio)
 *    aren't duplicated here either — the sibling "OI Flow Charts" tab
 *    already covers that ground from the same underlying history.
 *
 * 2. Density via MUI palette tokens only — NOT a nested dark theme.
 *    An earlier iteration wrapped this page in a locally-scoped dark
 *    "financial terminal" `ThemeProvider`; per direct follow-up feedback
 *    ("background is black, I want a background like OI Flow Charts —
 *    it should match with other page background"), that dark background
 *    was reverted so this page visually matches its own sibling tabs
 *    (OI Flow Charts, Strike Charts) and the rest of the app. Every
 *    `sx` color below (`success.main`, `background.paper`, `divider`,
 *    etc.) still uses MUI palette tokens rather than hardcoded hex, so
 *    it now simply resolves against TekInvest's one global (light)
 *    theme, same as every other page — no new theme file needed.
 *
 * 3. Correction (Option Analyst frontend symbol-state-sync fix): this
 *    point previously claimed a single-symbol (NIFTY/BANKNIFTY) selector
 *    "kept exactly as before." That was stale — the component below
 *    (`OptionIndicatorPage`) has always unconditionally rendered both a
 *    NIFTY and a BANKNIFTY `OptionIndicatorSymbolPanel` side by side, with
 *    no selector/dropdown anywhere in this file. That is in fact the
 *    reference's side-by-side dual-index layout, same pattern as
 *    `OIChartsPage`/`OIStrikeChartsPage`/`DualSymbolDashboardPage` — there
 *    is no single-symbol state here to sync with AI Insights/Strategy
 *    Builder, so this page needed no change for that fix.
 *
 * Also adds a compact "market ticker" header (reference Section A) using
 * only already-available fields (regime/size, local PCR, spot,
 * `is_open_session`, `expiry_date`/`dte`) plus a client-side wall clock
 * against NSE's well-known, public 09:15–15:30 IST session window (not
 * fabricated data — it's the same public fact the reference's own
 * "76m/375m" example implies: 15:30 − 09:15 = 375 minutes).
 */
import { indexSectionProps, useAnalystIndexView } from "@/context/AnalystIndexViewContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import optionIndicatorService from "@/services/optionIndicatorService";
import type {
  OIScoreSnapshot,
  OptionIndicatorAlertsRead,
  OptionIndicatorDashboardRead,
  OptionIndicatorSrStrengthScore,
} from "@/types";
import { extractErrorMessage } from "@/utils/errors";

// Monospace stack for numeric/technical values — the reference
// dashboard's own terminal-style number treatment.
const MONO_FONT = '"Roboto Mono", "SF Mono", Consolas, monospace';

function isNotFoundError(err: unknown): boolean {
  return Boolean(
    err &&
      typeof err === "object" &&
      "response" in err &&
      (err as { response?: { status?: number } }).response?.status === 404
  );
}

/** Every OIScoreSnapshot Decimal field arrives as a JSON string. */
function n(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function fmt(value: string | number | null | undefined, digits = 2): string {
  const parsed = n(value);
  return parsed === null ? "—" : parsed.toFixed(digits);
}

function fmtInt(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : value.toLocaleString("en-IN");
}

function directionColor(direction?: string | null): "success" | "error" | "default" {
  if (direction === "bull") return "success";
  if (direction === "bear") return "error";
  return "default";
}

/** Bearish/bullish framing for pin-strength / wall chips — green=support/
 * bullish, red=resistance/bearish, matching the terminal theme's own
 * accent-color convention (not the reference's exact palette values). */
function pinStatusColor(status?: string): "success" | "error" | "warning" | "default" {
  if (status === "STRENGTHENING") return "success";
  if (status === "BREAKING") return "error";
  if (status === "BEARISH BREAK" || status === "BULLISH BREAK") return "warning";
  return "default";
}

/** Static methodology labels for the 6 composite-score components —
 * descriptive copy about what each component measures (ported verbatim
 * from the same scoring engine's own documentation), not per-snapshot
 * data. Numeric values themselves always come from `compositeScore`. */
const COMPONENT_LABELS: Record<string, string> = {
  C1: "PCR Level + Slope (25%) — structural put/call ratio and its 30-min trend",
  C2: "Institutional Flow (25%) — today's fresh option writing/unwinding",
  C3: "VWOI Gap (15%) — near vs. wide-band premium; strongest confirmation signal",
  C4: "Volatility (15%) — straddle velocity plus PCR acceleration",
  C5: "Structure S/R (10%) — distance from the nearest put/call OI walls",
  C6: "Range Amplifier (10%) — amplifies or dampens C1-C5 via RTS",
};

/** Optimal-Slot-Advice descriptive text per time_quality band, with the
 * concrete validated time windows and Profit Factor (PF) stats ported
 * verbatim from oi_score_v2.py's TIME_SLOT_DESC / _time_quality bands
 * (gap-analysis "Optimal Slot Advice" item). */
const TIME_SLOT_DESC: Record<string, string> = {
  BEST: "BEST SLOT \u2014 historically strong PF for CE/PE entries (09:25-10:30, 12:00-12:30, 13:00-14:00; PF up to 1.99, best 13:30-14:00)",
  OK: "OK SLOT \u2014 average performance",
  AVOID: "AVOID \u2014 poor historical PF (11:00-11:30: PF 0.48 \u00b7 12:30-13:00: PF 0.82)",
};

/** Static exit-rules callout, ported verbatim from oi_score_v2.py's
 * EXIT_RULES constant (validated on 223 days) \u2014 fixed trading rules,
 * not computed per-bar (gap-analysis "Exit Guidelines Banner" item). */
const EXIT_RULES_TEXT = [
  "Primary: Close position 60 min after entry (PE peaks 45-60m, CE peaks 60-120m), or 15:15 if entered after 14:15.",
  "Stop: 150 pts adverse spot move \u2014 CATASTROPHE BLOCKER ONLY (tight stops <100pts destroy edge: 47-26% of trades stopped prematurely).",
  "Do NOT exit when score drops below threshold (market continues +10pts/60m after).",
  "Do NOT exit when position is briefly adverse at 15m (median MAE = -46pts before recovery).",
];

/** Sub-metric driver variables per component, re-integrated directly
 * inside each C1-C6 card (gap-analysis "Sub-Metrics Driver Variables"
 * item \u2014 previously only shown in the detailed metrics grid further
 * down the page; same compositeScore fields, also surfaced here). */
function componentSubMetric(label: string, s: OIScoreSnapshot): string {
  switch (label) {
    case "C1":
      return `Slope ${fmt(s.pcr_slope_score, 0)} \u00b7 Accel ${fmt(s.pcr_accel, 3)}`;
    case "C2":
      return `PutW/CallW ${fmt(s.pw_15m, 0)}/${fmt(s.cw_15m, 0)} \u00b7 MigFresh ${s.mig_fresh ?? "\u2014"}`;
    case "C3":
      return `VWOI Gap ${fmt(s.vwoi_gap, 3)}`;
    case "C4":
      return `Straddle ${fmt(s.straddle, 1)} (${fmt(s.straddle_vel, 1)}%)`;
    case "C5":
      return `Sup ${fmt(s.sup_dist, 0)} \u00b7 Res ${fmt(s.res_dist, 0)}`;
    case "C6":
      return `RTS ${fmt(s.rts, 3)} \u00b7 OIconc ${fmt(s.oi_conc, 4)}`;
    default:
      return "";
  }
}

/** Renders one OI-Dashboard-style "score gauge": a 0(bear)-10(bull) bar
 * with three static zones and a marker at the current score. Presentational
 * only — the same score_v2 value already shown numerically above it. */
function ScoreGauge({ score }: { score: number }) {
  const pct = Math.max(0, Math.min(100, (score / 10) * 100));
  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ position: "relative", height: 10, borderRadius: 1, overflow: "hidden", display: "flex" }}>
        <Box sx={{ width: "40%", bgcolor: "error.main" }} />
        <Box sx={{ width: "20%", bgcolor: "grey.600" }} />
        <Box sx={{ width: "40%", bgcolor: "success.main" }} />
        <Box
          sx={{
            position: "absolute",
            top: -3,
            left: `calc(${pct}% - 3px)`,
            width: 0,
            height: 0,
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderTop: "8px solid",
            borderTopColor: "text.primary",
          }}
        />
      </Box>
      <Stack direction="row" justifyContent="space-between" columnGap={1} sx={{ mt: 0.5 }}>
        {["0 Bear", "5 Neutral", "10 Bull"].map((label) => (
          <Typography key={label} variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", fontSize: "0.68rem" }}>
            {label}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

/** One row of the Support/Resistance Strength card. Backed entirely by
 * `dashboard.sr_strength_scores` (already fetched, previously unused). */
function SrStrengthRow({ row }: { row: OptionIndicatorSrStrengthScore }) {
  const isResistance = row.side === "RESISTANCE";
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ py: 0.5 }}>
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Chip
          size="small"
          label={row.side}
          color={isResistance ? "error" : "success"}
          variant="outlined"
          sx={{ fontFamily: MONO_FONT }}
        />
        <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
          {row.strike.toLocaleString("en-IN")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.label}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ fontFamily: MONO_FONT }} color="text.secondary">
        {row.score}/100
      </Typography>
    </Stack>
  );
}

/** Decodes `dashboard.max_pain_analysis` (a loosely-typed dict — see
 * `app.services.option_indicator.chain_signals.compute_max_pain_analysis`
 * / `compute_pin_strength`). Returns null if there weren't enough
 * distinct max-pain levels this snapshot for the backend to compute it
 * (it then sends `{}`). Strike values aren't sent directly — they're
 * derived here as `spot + dist`, exactly as the backend derived `dist`
 * from its own `strike - spot`. */
function decodeMaxPain(
  maxPainAnalysis: Record<string, unknown> | undefined,
  spot: number
): {
  mp1Strike: number;
  mp2Strike: number;
  mp1: Record<string, unknown>;
  mp2: Record<string, unknown>;
  inZone: boolean;
  zoneLo: number;
  zoneHi: number;
  // Option Analyst audit backlog item ("zone-skew"): where spot sits
  // within the pain zone, -1 (lower edge) .. 0 (centered) .. +1 (upper
  // edge) — see backend `compute_max_pain_analysis`'s docstring.
  // `null` on older cached responses / the zero-width edge case.
  zoneSkewLabel: string | null;
} | null {
  if (!maxPainAnalysis || Object.keys(maxPainAnalysis).length === 0) return null;
  const dist1 = n(maxPainAnalysis.dist1 as number | string | null);
  const dist2 = n(maxPainAnalysis.dist2 as number | string | null);
  const zoneLo = n(maxPainAnalysis.zone_lo as number | string | null);
  const zoneHi = n(maxPainAnalysis.zone_hi as number | string | null);
  if (dist1 === null || dist2 === null || zoneLo === null || zoneHi === null) return null;
  return {
    mp1Strike: spot + dist1,
    mp2Strike: spot + dist2,
    mp1: (maxPainAnalysis.mp1 as Record<string, unknown>) ?? {},
    mp2: (maxPainAnalysis.mp2 as Record<string, unknown>) ?? {},
    inZone: Boolean(maxPainAnalysis.in_zone),
    zoneLo,
    zoneHi,
    zoneSkewLabel: (maxPainAnalysis.zone_skew_label as string | undefined) ?? null,
  };
}

/** Horizontal momentum gauge for `pcr_slope_score` (range -5..+5, per
 * oi_dash.py's OLS-slope-of-local_pcr scoring) — gap-analysis "PCR Slope
 * visual gauge" item. Frontend-only: reuses the already-fetched
 * `compositeScore.pcr_slope_score` field, no new backend computation. */
function PcrSlopeGauge({ score }: { score: number }) {
  const clamped = Math.max(-5, Math.min(5, score));
  const pct = ((clamped + 5) / 10) * 100;
  const markerColor = clamped > 0 ? "success.main" : clamped < 0 ? "error.main" : "text.secondary";
  return (
    <Box sx={{ mt: 0.75, maxWidth: 220 }}>
      <Box
        sx={(theme) => ({
          position: "relative",
          height: 6,
          borderRadius: 3,
          background: `linear-gradient(to right, ${theme.palette.error.main} 0%, ${theme.palette.error.main} 45%, ${theme.palette.grey[400]} 50%, ${theme.palette.success.main} 55%, ${theme.palette.success.main} 100%)`,
        })}
      >
        <Box
          sx={{
            position: "absolute",
            top: -4,
            left: `calc(${pct}% - 6px)`,
            width: 12,
            height: 14,
            borderRadius: "3px",
            bgcolor: "background.paper",
            border: "2px solid",
            borderColor: markerColor,
          }}
        />
      </Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
          −5 Collapsing
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
          0 Flat
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
          +5 Building
        </Typography>
      </Stack>
    </Box>
  );
}

function PinStatusChip({ pin }: { pin: Record<string, unknown> }) {
  const status = typeof pin.status === "string" ? pin.status : undefined;
  const note = typeof pin.note === "string" ? pin.note : undefined;
  if (!status) return null;
  return (
    <Stack spacing={0.5}>
      <Chip size="small" label={status} color={pinStatusColor(status)} sx={{ fontFamily: MONO_FONT, width: "fit-content" }} />
      {note && (
        <Typography variant="caption" color="text.secondary">
          {note}
        </Typography>
      )}
    </Stack>
  );
}

/**
 * OI Dashboard sections with no corresponding backend field today.
 * Disclosed explicitly rather than omitted silently or faked — same
 * convention as `OIChartsPage.tsx`'s own "Not Yet Available" panel.
 */
const NOT_YET_AVAILABLE = [
  "OI Flow by timeframe (5/10/15-min buckets)",
  "Strike velocity (fastest single strike)",
  "OI vs. price divergence",
  "New strike activation",
  "Roll detection",
  "PCR slope 30-min trend & projection",
];

// NSE's regular equity/derivatives session: 09:15-15:30 IST, 375 minutes
// total — a public, fixed fact (matches the reference's own "76m/375m"
// example), not something computed or fabricated per-snapshot.
const MARKET_OPEN_MIN = 9 * 60 + 15;
const MARKET_CLOSE_MIN = 15 * 60 + 30;
const SESSION_LENGTH_MIN = MARKET_CLOSE_MIN - MARKET_OPEN_MIN;

function useIstClockMinutes(): number {
  const [minutes, setMinutes] = useState<number>(() => istMinutesNow());
  useEffect(() => {
    const id = setInterval(() => setMinutes(istMinutesNow()), 30_000);
    return () => clearInterval(id);
  }, []);
  return minutes;
}

function istMinutesNow(): number {
  const istNow = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  return istNow.getUTCHours() * 60 + istNow.getUTCMinutes();
}
/** Mon=1 ... Sun=0, computed against the same IST-shifted clock as
 * istMinutesNow() — used to gate auto-refresh polling to trading days. */
function istWeekdayNow(): number {
  const istNow = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  return istNow.getUTCDay();
}

/** "HH:MM" (IST) of an ISO timestamp -- the scored bar's own time. */
function istHhmm(iso: string): string {
  const d = new Date(new Date(iso).getTime() + 5.5 * 60 * 60 * 1000);
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

/** True when NSE's regular session (09:15-15:30 IST, Mon-Fri) is not running
 * right now. */
function isMarketClosedNow(nowMin: number): boolean {
  const weekday = istWeekdayNow();
  return weekday === 0 || weekday === 6 || nowMin < MARKET_OPEN_MIN || nowMin >= MARKET_CLOSE_MIN;
}

/** Compact top "market ticker" strip (reference Section A). Every field
 * either already exists on `compositeScore`/`dashboard`, or is plain
 * client-side wall-clock math against NSE's public session hours. */
function MarketTickerBar({
  symbol,
  compositeScore,
  spot,
}: {
  symbol: string;
  compositeScore: OIScoreSnapshot | null;
  spot: number | null;
}) {
  const nowMin = useIstClockMinutes();
  const sessionElapsed = Math.max(0, Math.min(SESSION_LENGTH_MIN, nowMin - MARKET_OPEN_MIN));
  const timeToClose = Math.max(0, MARKET_CLOSE_MIN - nowMin);
  const hh = String(Math.floor(nowMin / 60)).padStart(2, "0");
  const mm = String(nowMin % 60).padStart(2, "0");

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        mb: 1.5,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 2,
        fontFamily: MONO_FONT,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          label={symbol}
          color="primary"
          sx={{ fontFamily: MONO_FONT, fontWeight: 700 }}
        />
        {compositeScore && (
          <>
            <Chip
              size="small"
              label={`${compositeScore.regime} · ${compositeScore.regime_name}`}
              color={directionColor(compositeScore.direction)}
              sx={{ fontFamily: MONO_FONT }}
            />
            <Typography variant="caption" sx={{ fontFamily: MONO_FONT }} color="text.secondary">
              PCR {fmt(compositeScore.local_pcr, 3)}
            </Typography>
          </>
        )}
        {spot != null && (
          <Typography variant="body2" sx={{ fontFamily: MONO_FONT }} fontWeight={700}>
            {spot.toLocaleString("en-IN")}
          </Typography>
        )}
      </Stack>
      <Divider orientation="vertical" flexItem sx={{ borderColor: "divider" }} />
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            MARKET TIME
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
            {hh}:{mm}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            SESSION PROGRESS
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
            {sessionElapsed}m / {SESSION_LENGTH_MIN}m
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            TIME TO CLOSE
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
            {timeToClose}m
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            ENTRY WINDOW
          </Typography>
          <Chip
            size="small"
            label={compositeScore?.is_open_session ? "OPEN" : "CLOSED"}
            color={compositeScore?.is_open_session ? "success" : "error"}
            sx={{ fontFamily: MONO_FONT, height: 20 }}
          />
        </Box>
        {compositeScore && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              EXPIRY
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
              {compositeScore.expiry_date} (DTE {compositeScore.dte})
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function OptionIndicatorSymbolPanel({ symbol }: { symbol: string }) {
  const nowMinutes = useIstClockMinutes();
  const marketClosedNow = isMarketClosedNow(nowMinutes);
  const [dashboard, setDashboard] = useState<OptionIndicatorDashboardRead | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [noDataYet, setNoDataYet] = useState(false);
  const [alerts, setAlerts] = useState<OptionIndicatorAlertsRead | null>(null);
  const [alertsError, setAlertsError] = useState<string | null>(null);
  const [alertPriorityFilter, setAlertPriorityFilter] = useState<"ALL" | "P1" | "P2">("ALL");
  const [compositeScore, setCompositeScore] = useState<OIScoreSnapshot | null>(null);
  const [compositeScoreLoading, setCompositeScoreLoading] = useState(true);
  const [compositeScoreError, setCompositeScoreError] = useState<string | null>(null);
  const [compositeScoreNoData, setCompositeScoreNoData] = useState(false);
  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    setDashboardError(null);
    setNoDataYet(false);
    try {
      const result = await optionIndicatorService.getDashboard(symbol);
      setDashboard(result);
    } catch (err) {
      if (isNotFoundError(err)) {
        setNoDataYet(true);
        setDashboard(null);
      } else {
        setDashboardError(extractErrorMessage(err));
      }
    } finally {
      setDashboardLoading(false);
    }
  }, [symbol]);

  const loadAlerts = useCallback(async () => {
    try {
      const result = await optionIndicatorService.getAlerts(symbol, 20);
      setAlerts(result);
      setAlertsError(null);
    } catch (err) {
      if (!isNotFoundError(err)) {
        setAlertsError(extractErrorMessage(err));
      }
      setAlerts(null);
    }
  }, [symbol]);

  const loadCompositeScore = useCallback(async () => {
    setCompositeScoreLoading(true);
    setCompositeScoreError(null);
    setCompositeScoreNoData(false);
    try {
      const result = await optionIndicatorService.getCompositeScore(symbol);
      setCompositeScore(result);
    } catch (err) {
      if (isNotFoundError(err)) {
        setCompositeScoreNoData(true);
        setCompositeScore(null);
      } else {
        setCompositeScoreError(extractErrorMessage(err));
      }
    } finally {
      setCompositeScoreLoading(false);
    }
  }, [symbol]);
  // Single entry point for every refresh path (mount, 60s auto-refresh,
  // manual Refresh button, Run Now) so "last refreshed" always reflects
  // the true last time data was pulled, however it was triggered.
  const refreshAll = useCallback(async () => {
    await Promise.all([loadDashboard(), loadAlerts(), loadCompositeScore()]);
  }, [loadDashboard, loadAlerts, loadCompositeScore]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);
  // Auto-refresh every 60s, but only during NSE market hours
  // (Mon-Fri, 09:15-15:30 IST) — outside that window the data can't
  // change (scheduler itself is market-hours gated), so polling would
  // just be wasted requests.
  const autoRefreshClockMinutes = useIstClockMinutes();
  const isMarketHoursNow = useMemo(() => {
    const weekday = istWeekdayNow();
    const isWeekday = weekday >= 1 && weekday <= 5;
    return (
      isWeekday &&
      autoRefreshClockMinutes >= MARKET_OPEN_MIN &&
      autoRefreshClockMinutes <= MARKET_CLOSE_MIN
    );
  }, [autoRefreshClockMinutes]);
  useEffect(() => {
    if (!isMarketHoursNow) return undefined;
    const id = setInterval(() => {
      refreshAll();
    }, 60_000);
    return () => clearInterval(id);
  }, [isMarketHoursNow, refreshAll]);


  const maxPain = dashboard ? decodeMaxPain(dashboard.max_pain_analysis, dashboard.spot) : null;
  const srSupport = dashboard?.sr_strength_scores.filter((r) => r.side === "SUPPORT") ?? [];
  const srResistance = dashboard?.sr_strength_scores.filter((r) => r.side === "RESISTANCE") ?? [];
  const spot = useMemo(() => dashboard?.spot ?? null, [dashboard]);

  return (
    <Box sx={{ bgcolor: "background.default", color: "text.primary", p: 1, borderRadius: 1 }}>
      <MarketTickerBar symbol={symbol} compositeScore={compositeScore} spot={spot} />

        {/* ---------------------------------------------------------------- */}
        {/* Composite Score v2 — expanded with every already-fetched         */}
        {/* OIScoreSnapshot field the previous version of this page dropped. */}
        {/* ---------------------------------------------------------------- */}
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 1.5,
            borderLeft: 4,
            borderLeftColor: compositeScore ? `${directionColor(compositeScore.direction)}.main` : "divider",
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            OI Composite Score v2 (0-10)
          </Typography>
          {compositeScoreLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : compositeScoreNoData ? (
            <Typography variant="body2" color="text.secondary">
              No composite score yet for {symbol}.
            </Typography>
          ) : compositeScoreError ? (
            <Alert severity="warning">{compositeScoreError}</Alert>
          ) : compositeScore ? (
            <Grid container spacing={1.5} alignItems="flex-start">
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Score v2
                </Typography>
                <Typography variant="h4" fontWeight={700} sx={{ fontFamily: MONO_FONT }}>
                  {fmt(compositeScore.score_v2)}
                  <Typography component="span" variant="body2" color="text.secondary">
                    {" "}
                    /10
                  </Typography>
                </Typography>
                <Chip
                  size="small"
                  label={`${compositeScore.regime} · ${compositeScore.regime_name}`}
                  color={directionColor(compositeScore.direction)}
                  sx={{ mt: 0.5 }}
                />
                <ScoreGauge score={n(compositeScore.score_v2) ?? 5} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  G3 Gate
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {compositeScore.g3_zone ?? "Neutral zone"}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Position size: {compositeScore.position_size}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Session: {compositeScore.is_open_session ? "Entry window OPEN" : "Entry window closed"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Decision
                </Typography>
                <Typography variant="body2">{compositeScore.decision}</Typography>
                {/* The slot advice describes the SCORED BAR's time of day. When
                    the market is closed right now (e.g. viewing after 15:30),
                    say so instead of presenting that bar's "BEST SLOT" as
                    current advice. */}
                <Typography variant="caption" color="text.secondary">
                  {marketClosedNow
                    ? `Time quality (last scored bar, ${istHhmm(compositeScore.captured_at)} IST): ${compositeScore.time_quality}`
                    : `Time quality: ${compositeScore.time_quality}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {marketClosedNow
                    ? "Market closed — slot advice applies to that bar only, not to a new entry."
                    : (TIME_SLOT_DESC[compositeScore.time_quality] ?? "")}
                </Typography>
                {compositeScore.regime_note && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {compositeScore.regime_note}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Components
                </Typography>
                <Grid container spacing={1}>
                  {(["C1", "C2", "C3", "C4", "C5", "C6"] as const).map((label) => {
                    const value = compositeScore.components[label];
                    return (
                      <Grid item xs={12} sm={6} md={4} key={label}>
                        <Stack direction="row" spacing={1} alignItems="baseline">
                          <Typography variant="body2" fontWeight={700} sx={{ fontFamily: MONO_FONT }}>
                            {label}: {fmt(value)}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {COMPONENT_LABELS[label]}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ fontFamily: MONO_FONT }}
                        >
                          {componentSubMetric(label, compositeScore)}
                        </Typography>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>

              {compositeScore.confirmation && (
                <Grid item xs={12}>
                  <Typography variant="body2" fontWeight={600} gutterBottom sx={{ mt: 1 }}>
                    Confirmation ({String(compositeScore.confirmation.sizing ?? "—")})
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Signal</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>C3 (VWOI Gap)</TableCell>
                          <TableCell>{String(compositeScore.confirmation.C3_status ?? "—")}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>C2 (Institutional Flow)</TableCell>
                          <TableCell>{String(compositeScore.confirmation.C2_status ?? "—")}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Strike migration</TableCell>
                          <TableCell>{String(compositeScore.confirmation.mig_status ?? "—")}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Local PCR (level {fmt(compositeScore.pcr_level, 0)})
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                      {fmt(compositeScore.local_pcr, 3)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Slope score {fmt(compositeScore.pcr_slope_score, 0)} · Accel {fmt(compositeScore.pcr_accel, 3)}
                    </Typography>
                    <PcrSlopeGauge score={compositeScore.pcr_slope_score} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      VWOI Gap
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                      {fmt(compositeScore.vwoi_gap, 3)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Net flow (cum) {fmtInt(n(compositeScore.net_flow_cum))} · bar {fmt(compositeScore.net_flow_bar, 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Straddle
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                      {fmt(compositeScore.straddle, 1)} ({fmt(compositeScore.straddle_vel, 1)}%)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      DTE {compositeScore.dte} (×{fmt(compositeScore.dte_mult, 2)}) · VolGate {fmt(compositeScore.vol_gate, 2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Structure
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                      Sup {fmt(compositeScore.sup_dist, 0)} · Res {fmt(compositeScore.res_dist, 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      RTS {fmt(compositeScore.rts, 3)} · OIconc {fmt(compositeScore.oi_conc, 4)}
                    </Typography>
                  </Grid>
                </Grid>
                {(compositeScore.top_put_strike || compositeScore.top_call_strike) && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Top put OI strike {fmt(compositeScore.top_put_strike, 0)} · Top call OI strike{" "}
                    {fmt(compositeScore.top_call_strike, 0)} · Range (30m) {fmt(compositeScore.range_30m, 1)}
                  </Typography>
                )}
              </Grid>
            </Grid>
          ) : null}
        </Paper>
        {compositeScore && (
          <Alert severity="info" sx={{ fontFamily: MONO_FONT }}>
            <Typography variant="caption" fontWeight={700} display="block">
              EXIT GUIDELINES (validated on 223 days)
            </Typography>
            {EXIT_RULES_TEXT.map((line, i) => (
              <Typography key={i} variant="caption" display="block">
                {line}
              </Typography>
            ))}
          </Alert>
        )}

        {dashboardLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : noDataYet ? (
          <Alert severity="info">
            No Option Indicator data yet for {symbol}. Check back once the scheduler has run.
          </Alert>
        ) : dashboardError ? (
          <Alert severity="error">{dashboardError}</Alert>
        ) : dashboard ? (
          <Stack spacing={1.5}>
            {/* ---- Market summary strip ---- */}
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={3}>
                <Card variant="outlined" sx={{ borderLeft: 4, borderLeftColor: "primary.main" }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Spot
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ fontFamily: MONO_FONT }}>
                      {dashboard.spot.toLocaleString("en-IN")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      As of {new Date(dashboard.captured_at).toLocaleString("en-IN")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card variant="outlined" sx={{ borderLeft: 4, borderLeftColor: "info.main" }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Fresh PCR (local)
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ fontFamily: MONO_FONT }}>
                      {dashboard.fresh_pcr.fresh_pcr_local.toFixed(3)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dashboard.fresh_pcr.fresh_pcr_label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card variant="outlined" sx={{ borderLeft: 4, borderLeftColor: "info.main" }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Volume PCR (local)
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ fontFamily: MONO_FONT }}>
                      {dashboard.volume_pcr.vol_pcr_local.toFixed(3)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dashboard.volume_pcr.vol_pcr_label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card variant="outlined" sx={{ borderLeft: 4, borderLeftColor: "warning.main" }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Gamma Exposure
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ fontFamily: MONO_FONT }}>
                      {dashboard.gex.gex_total_cr != null ? `${dashboard.gex.gex_total_cr.toFixed(0)} Cr` : "—"}
                    </Typography>
                    <Chip size="small" label={dashboard.gex.gex_sign} sx={{ mt: 0.5 }} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* ---- GEX detail ---- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                GEX (Gamma Exposure) — {dashboard.gex.gex_bias}
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Call / Put GEX
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                    {fmt(dashboard.gex.gex_call_cr, 0)} Cr / {fmt(dashboard.gex.gex_put_cr, 0)} Cr
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Flip level
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                    {dashboard.gex.gex_flip_level != null ? dashboard.gex.gex_flip_level.toLocaleString("en-IN") : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Label
                  </Typography>
                  <Typography variant="body2">{dashboard.gex.gex_label}</Typography>
                </Grid>
              </Grid>
              {dashboard.gex.top_gex_strikes.length > 0 && (
                <TableContainer sx={{ mt: 1.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Strike</TableCell>
                        <TableCell align="right">Call IV</TableCell>
                        <TableCell align="right">Put IV</TableCell>
                        <TableCell align="right">Net GEX (Cr)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboard.gex.top_gex_strikes.map((s) => (
                        <TableRow key={s.strike}>
                          <TableCell sx={{ fontFamily: MONO_FONT }}>{s.strike.toLocaleString("en-IN")}</TableCell>
                          <TableCell align="right">{s.c_iv.toFixed(1)}%</TableCell>
                          <TableCell align="right">{s.p_iv.toFixed(1)}%</TableCell>
                          <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                            {s.net_gex_cr.toFixed(1)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* ---- Volume PCR detail ---- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Volume PCR — {dashboard.volume_pcr.pressure_label}
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Traded volume (Call / Put)
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                    {dashboard.volume_pcr.vol_call_total_L.toFixed(1)}L / {dashboard.volume_pcr.vol_put_total_L.toFixed(1)}L
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Absorption (Call / Put)
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                    {fmt(dashboard.volume_pcr.absorption_call, 3)} / {fmt(dashboard.volume_pcr.absorption_put, 3)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dashboard.volume_pcr.absorption_call_label} · {dashboard.volume_pcr.absorption_put_label}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Top call volume strike
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                    {dashboard.volume_pcr.top_call_vol_strike.toLocaleString("en-IN")} ({dashboard.volume_pcr.top_call_vol_L.toFixed(1)}L)
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Top put volume strike
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                    {dashboard.volume_pcr.top_put_vol_strike.toLocaleString("en-IN")} ({dashboard.volume_pcr.top_put_vol_L.toFixed(1)}L)
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* ---- Zone Skew (Near / Mid / Far Fresh PCR) ---- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Zone Skew (Fresh PCR by distance from spot)
              </Typography>
              <Grid container spacing={1.5}>
                {(["near", "mid", "far"] as const).map((zone) => {
                  const z = dashboard.zone_skew?.[zone];
                  return (
                    <Grid item xs={12} sm={4} key={zone}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: "uppercase" }}>
                        {zone}
                      </Typography>
                      <Typography variant="body1" fontWeight={700} sx={{ fontFamily: MONO_FONT }}>
                        {z ? z.fresh_pcr_local.toFixed(3) : "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {z ? z.fresh_pcr_label : "No data"}
                      </Typography>
                    </Grid>
                  );
                })}
              </Grid>
              {dashboard.zone_divergence && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {dashboard.zone_divergence}
                </Typography>
              )}
            </Paper>

            {/* ---- ATM CE/PE Ratio & Expected Daily Move ---- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                ATM CE/PE Ratio & Volatility
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    ATM strike
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                    {dashboard.atm_ce_pe_ratio ? dashboard.atm_ce_pe_ratio.atm_strike.toLocaleString("en-IN") : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    CE/PE OI ratio (put/call)
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                    {dashboard.atm_ce_pe_ratio?.ce_pe_ratio != null ? dashboard.atm_ce_pe_ratio.ce_pe_ratio.toFixed(3) : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Raw ATM IV %
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                    {dashboard.atm_iv_avg != null ? `${dashboard.atm_iv_avg.toFixed(2)}%` : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Expected daily move
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                    {dashboard.expected_daily_move_pts != null ? `± ${dashboard.expected_daily_move_pts.toFixed(0)} pts` : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Straddle OI (ATM CE+PE)
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                    {dashboard.atm_ce_pe_ratio ? dashboard.atm_ce_pe_ratio.straddle_oi.toLocaleString("en-IN") : "—"}
                    {dashboard.atm_ce_pe_ratio
                      ? ` (Δ ${dashboard.atm_ce_pe_ratio.straddle_oi_chg >= 0 ? "+" : ""}${dashboard.atm_ce_pe_ratio.straddle_oi_chg.toLocaleString("en-IN")})`
                      : ""}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Straddle status
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={
                      dashboard.atm_ce_pe_ratio?.straddle_status === "BUILDING"
                        ? "success.main"
                        : dashboard.atm_ce_pe_ratio?.straddle_status === "COLLAPSING"
                        ? "error.main"
                        : "text.primary"
                    }
                  >
                    {dashboard.atm_ce_pe_ratio?.straddle_label ?? "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    ATM call Δ / put Δ (intraday)
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                    {dashboard.atm_ce_pe_ratio
                      ? `${dashboard.atm_ce_pe_ratio.atm_call_chg.toLocaleString("en-IN")} / ${dashboard.atm_ce_pe_ratio.atm_put_chg.toLocaleString("en-IN")}`
                      : "—"}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* ---- Strike Velocity ---- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Strike Velocity (fastest OI acceleration this snapshot)
              </Typography>
              {dashboard.oi_intelligence.strike_velocity ? (
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Fastest call build
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: MONO_FONT }} color="error.main">
                      {dashboard.oi_intelligence.strike_velocity.sv_call_strike.toLocaleString("en-IN")} (+
                      {dashboard.oi_intelligence.strike_velocity.sv_call_chg.toLocaleString("en-IN")})
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Fastest call unwind
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: MONO_FONT }} color="success.main">
                      {dashboard.oi_intelligence.strike_velocity.sv_call_unwind_strike.toLocaleString("en-IN")} (
                      {dashboard.oi_intelligence.strike_velocity.sv_call_unwind_chg.toLocaleString("en-IN")})
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Fastest put build
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: MONO_FONT }} color="success.main">
                      {dashboard.oi_intelligence.strike_velocity.sv_put_strike.toLocaleString("en-IN")} (+
                      {dashboard.oi_intelligence.strike_velocity.sv_put_chg.toLocaleString("en-IN")})
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Fastest put unwind
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: MONO_FONT }} color="error.main">
                      {dashboard.oi_intelligence.strike_velocity.sv_put_unwind_strike.toLocaleString("en-IN")} (
                      {dashboard.oi_intelligence.strike_velocity.sv_put_unwind_chg.toLocaleString("en-IN")})
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not enough chain data this snapshot.
                </Typography>
              )}
            </Paper>

            {/* ---- OI / Price Divergence ---- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                OI / Price Divergence
              </Typography>
              <Alert
                severity={
                  dashboard.oi_intelligence.oi_divergence.divergence_bias === "bull"
                    ? "success"
                    : dashboard.oi_intelligence.oi_divergence.divergence_bias === "bear"
                    ? "error"
                    : "info"
                }
                icon={false}
              >
                {dashboard.oi_intelligence.oi_divergence.divergence_label}
              </Alert>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Spot Δ this bar: {dashboard.oi_intelligence.oi_divergence.price_delta} pts
              </Typography>
            </Paper>

            {/* ---- New Strike Activation ---- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                New Strike Activation
              </Typography>
              <Typography variant="body2" gutterBottom>
                {dashboard.oi_intelligence.new_strike_activation.label}
              </Typography>
              {(dashboard.oi_intelligence.new_strike_activation.new_call_strikes.length > 0 ||
                dashboard.oi_intelligence.new_strike_activation.new_put_strikes.length > 0) && (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} color="error.main" gutterBottom>
                      New call strikes
                    </Typography>
                    {dashboard.oi_intelligence.new_strike_activation.new_call_strikes.map((s) => (
                      <Typography key={s.strike} variant="caption" display="block" sx={{ fontFamily: MONO_FONT }}>
                        {s.strike.toLocaleString("en-IN")} — OI {s.oi.toLocaleString("en-IN")} (+{s.chg.toLocaleString("en-IN")})
                      </Typography>
                    ))}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} color="success.main" gutterBottom>
                      New put strikes
                    </Typography>
                    {dashboard.oi_intelligence.new_strike_activation.new_put_strikes.map((s) => (
                      <Typography key={s.strike} variant="caption" display="block" sx={{ fontFamily: MONO_FONT }}>
                        {s.strike.toLocaleString("en-IN")} — OI {s.oi.toLocaleString("en-IN")} (+{s.chg.toLocaleString("en-IN")})
                      </Typography>
                    ))}
                  </Grid>
                </Grid>
              )}
            </Paper>

            {/* ---- Roll Detection ---- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Roll Detection
              </Typography>
              <Typography variant="body2" gutterBottom>
                {dashboard.oi_intelligence.roll_detection.roll_label}
              </Typography>
              {dashboard.oi_intelligence.roll_detection.rolls_detected.length > 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Side</TableCell>
                        <TableCell align="right">From</TableCell>
                        <TableCell align="right">To</TableCell>
                        <TableCell align="right">Size</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboard.oi_intelligence.roll_detection.rolls_detected.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{r.side}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                            {r.from.toLocaleString("en-IN")}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                            {r.to.toLocaleString("en-IN")}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                            {r.size.toLocaleString("en-IN")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* ---- OI Concentration ---- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                OI Concentration (top-3 local strikes)
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Call concentration
                  </Typography>
                  <Typography variant="body1" fontWeight={700} sx={{ fontFamily: MONO_FONT }}>
                    {dashboard.oi_intelligence.oi_concentration.call_concentration_pct.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dashboard.oi_intelligence.oi_concentration.call_conc_label}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Put concentration
                  </Typography>
                  <Typography variant="body1" fontWeight={700} sx={{ fontFamily: MONO_FONT }}>
                    {dashboard.oi_intelligence.oi_concentration.put_concentration_pct.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dashboard.oi_intelligence.oi_concentration.put_conc_label}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* ---- Flow by Timeframe (5/10/15 min) ---- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Flow by Timeframe
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Window</TableCell>
                      <TableCell align="right">Net call flow (L)</TableCell>
                      <TableCell align="right">Net put flow (L)</TableCell>
                      <TableCell align="right">Fresh PCR</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>5 min</TableCell>
                      <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                        {dashboard.strike_conviction.net_call_flow_5min.toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                        {dashboard.strike_conviction.net_put_flow_5min.toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                        {dashboard.strike_conviction.pcr_5min != null ? dashboard.strike_conviction.pcr_5min.toFixed(3) : "—"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>10 min</TableCell>
                      <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                        {dashboard.strike_conviction.net_call_flow_10min.toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                        {dashboard.strike_conviction.net_put_flow_10min.toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                        {dashboard.strike_conviction.pcr_10min != null ? dashboard.strike_conviction.pcr_10min.toFixed(3) : "—"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>15 min</TableCell>
                      <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                        {dashboard.strike_conviction.net_call_flow_15min.toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                        {dashboard.strike_conviction.net_put_flow_15min.toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                        {dashboard.strike_conviction.pcr_15min != null ? dashboard.strike_conviction.pcr_15min.toFixed(3) : "—"}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* ---- Sustained Strike Confirmation ---- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Sustained Strike Confirmation
              </Typography>
              <Alert severity="info" icon={false} sx={{ mb: 1.5 }}>
                {dashboard.strike_conviction.conviction_summary}
              </Alert>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" fontWeight={600} color="error.main" gutterBottom>
                    Sustained call writing ({dashboard.strike_conviction.sustained_call_count})
                  </Typography>
                  {dashboard.strike_conviction.sustained_calls.length > 0 ? (
                    dashboard.strike_conviction.sustained_calls.map((s) => (
                      <Box key={s.strike} sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                          {s.strike.toLocaleString("en-IN")}CE — {s.trend} ({s.score_3}/3, +{s.chg_L}L)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.label}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      None this snapshot.
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" fontWeight={600} color="success.main" gutterBottom>
                    Sustained put writing ({dashboard.strike_conviction.sustained_put_count})
                  </Typography>
                  {dashboard.strike_conviction.sustained_puts.length > 0 ? (
                    dashboard.strike_conviction.sustained_puts.map((s) => (
                      <Box key={s.strike} sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                          {s.strike.toLocaleString("en-IN")}PE — {s.trend} ({s.score_3}/3, +{s.chg_L}L)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.label}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      None this snapshot.
                    </Typography>
                  )}
                </Grid>
              </Grid>
              {dashboard.strike_conviction.rolls_confirmed.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Roll-confirmed strikes (deducted from conviction above): {dashboard.strike_conviction.rolls_confirmed.join(", ")}
                </Typography>
              )}
            </Paper>

            {/* ---- Max Pain ---- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Max Pain
              </Typography>
              {maxPain ? (
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      MP1 — {maxPain.mp1Strike.toLocaleString("en-IN")}
                    </Typography>
                    <PinStatusChip pin={maxPain.mp1} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      MP2 — {maxPain.mp2Strike.toLocaleString("en-IN")}
                    </Typography>
                    <PinStatusChip pin={maxPain.mp2} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Pain zone {maxPain.zoneLo.toLocaleString("en-IN")} – {maxPain.zoneHi.toLocaleString("en-IN")} ·{" "}
                      Spot is {maxPain.inZone ? "inside" : "outside"} the zone
                      {maxPain.zoneSkewLabel ? ` · ${maxPain.zoneSkewLabel}` : ""}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not enough distinct max-pain levels this snapshot.
                </Typography>
              )}
            </Paper>

            {/* ---- Support / Resistance Strength (sr_strength_scores) ---- */}
            {(srSupport.length > 0 || srResistance.length > 0) && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Support / Resistance Strength
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} color="error.main" gutterBottom>
                      Resistance (above spot)
                    </Typography>
                    {srResistance.length > 0 ? (
                      srResistance.map((r) => <SrStrengthRow key={r.strike} row={r} />)
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        None this snapshot.
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} color="success.main" gutterBottom>
                      Support (below spot)
                    </Typography>
                    {srSupport.length > 0 ? (
                      srSupport.map((r) => <SrStrengthRow key={r.strike} row={r} />)
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        None this snapshot.
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* ---- Signals ---- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Signals
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" fontWeight={600}>
                    Institutional Footprint
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: MONO_FONT }}>
                    Bullish: {dashboard.institutional_footprint_bullish ?? "—"} · Bearish:{" "}
                    {dashboard.institutional_footprint_bearish ?? "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" fontWeight={600}>
                    Breakout Signal
                  </Typography>
                  {dashboard.breakout_signal ? (
                    <Alert severity="warning" sx={{ mt: 0.5 }} icon={false}>
                      {dashboard.breakout_signal}
                    </Alert>
                  ) : (
                    <Typography variant="body2">None</Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>

            {/* ---- Historical Percentiles (all 5 metrics) ---- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Historical Percentiles (last {dashboard.percentiles.n_sessions_local_pcr} sessions)
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" fontWeight={600}>
                    Local PCR percentile
                  </Typography>
                  <Typography variant="body2">{dashboard.percentiles.local_pcr_label}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" fontWeight={600}>
                    Volume PCR percentile
                  </Typography>
                  <Typography variant="body2">{dashboard.percentiles.vol_pcr_local_label}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" fontWeight={600}>
                    ATM IV percentile
                  </Typography>
                  <Typography variant="body2">{dashboard.percentiles.atm_iv_label}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" fontWeight={600}>
                    GEX percentile
                  </Typography>
                  <Typography variant="body2">{dashboard.percentiles.gex_total_cr_label}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" fontWeight={600}>
                    Fresh PCR percentile
                  </Typography>
                  <Typography variant="body2">
                    {dashboard.percentiles.fresh_pcr_local_percentile != null
                      ? `${dashboard.percentiles.fresh_pcr_local_percentile.toFixed(0)}th pct (${dashboard.percentiles.n_sessions_fresh_pcr} sessions)`
                      : "NO HISTORY — not enough sessions yet"}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* ---- Strike Migration Trail ---- */}
            {dashboard.strike_migration_trail.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Strike Migration Trail (last {dashboard.strike_migration_trail.length} bars)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        <TableCell align="right">Top call OI strike</TableCell>
                        <TableCell align="right">Top put OI strike</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboard.strike_migration_trail.map((point, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ fontFamily: MONO_FONT, whiteSpace: "nowrap" }}>
                            {new Date(point.captured_at).toLocaleTimeString("en-IN", { hour12: false })}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                            {point.top_call_strike != null ? point.top_call_strike.toLocaleString("en-IN") : "—"}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                            {point.top_put_strike != null ? point.top_put_strike.toLocaleString("en-IN") : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* ---- Fresh Writing (per-strike) ---- */}
            {(dashboard.fresh_call_list.length > 0 || dashboard.fresh_put_list.length > 0) && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Fresh Option Writing (per-strike, OTM only)
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} color="error.main" gutterBottom>
                      Fresh call writing (above spot)
                    </Typography>
                    {dashboard.fresh_call_list.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableBody>
                            {dashboard.fresh_call_list.map((row, i) => (
                              <TableRow key={i}>
                                <TableCell sx={{ fontFamily: MONO_FONT }}>{String(row.strike)}</TableCell>
                                <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                                  +{Number(row.call_chg).toLocaleString("en-IN")}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        None this snapshot.
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} color="success.main" gutterBottom>
                      Fresh put writing (below spot)
                    </Typography>
                    {dashboard.fresh_put_list.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableBody>
                            {dashboard.fresh_put_list.map((row, i) => (
                              <TableRow key={i}>
                                <TableCell sx={{ fontFamily: MONO_FONT }}>{String(row.strike)}</TableCell>
                                <TableCell align="right" sx={{ fontFamily: MONO_FONT }}>
                                  +{Number(row.put_chg).toLocaleString("en-IN")}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        None this snapshot.
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* ---- OI Unwinding (per-strike) ---- */}
            {(dashboard.unwind_totals.call_unwind.length > 0 || dashboard.unwind_totals.put_unwind.length > 0) && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  OI Unwinding (per-strike) — Calls {fmtInt(dashboard.unwind_totals.uw_calls)} · Puts{" "}
                  {fmtInt(dashboard.unwind_totals.uw_puts)}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Call unwind
                    </Typography>
                    {dashboard.unwind_totals.call_unwind.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Strike</TableCell>
                              <TableCell>Zone</TableCell>
                              <TableCell>Signal</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dashboard.unwind_totals.call_unwind.map((row, i) => (
                              <TableRow key={i}>
                                <TableCell sx={{ fontFamily: MONO_FONT }}>{String(row.strike)}</TableCell>
                                <TableCell>{String(row.zone)}</TableCell>
                                <TableCell>{String(row.signal)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        None this snapshot.
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Put unwind
                    </Typography>
                    {dashboard.unwind_totals.put_unwind.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Strike</TableCell>
                              <TableCell>Zone</TableCell>
                              <TableCell>Signal</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dashboard.unwind_totals.put_unwind.map((row, i) => (
                              <TableRow key={i}>
                                <TableCell sx={{ fontFamily: MONO_FONT }}>{String(row.strike)}</TableCell>
                                <TableCell>{String(row.zone)}</TableCell>
                                <TableCell>{String(row.signal)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        None this snapshot.
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* ---- Narrative ---- */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight={700}>
                  Narrative
                </Typography>
                <Chip size="small" label={`${dashboard.narrative.signal_icon} ${dashboard.narrative.signal}`} />
              </Stack>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {dashboard.narrative.structure}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {dashboard.narrative.intraday}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
                {dashboard.narrative.interpretation}
              </Typography>
              {dashboard.narrative.warnings.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  {dashboard.narrative.warnings.map((w, i) => (
                    <Alert key={i} severity="warning" sx={{ mb: 1 }}>
                      {w}
                    </Alert>
                  ))}
                </Box>
              )}
              {dashboard.narrative.actions.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Suggested actions
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {dashboard.narrative.actions.map((a, i) => (
                      <li key={i}>
                        <Typography variant="body2">{a}</Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
              )}
            </Paper>
          </Stack>
        ) : null}

        {/* ---------------------------------------------------------------- */}
        {/* Disclosed gaps — OI Dashboard sections with no backend data today */}
        {/* ---------------------------------------------------------------- */}
        <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, bgcolor: "action.hover" }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <InfoOutlinedIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={700}>
              Not Yet Available
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            The reference OI Dashboard also shows the sections below. TekInvest doesn't compute or persist this data
            yet, so rather than fake it, it's disclosed here explicitly. Time-series charts (PCR+VWOI, Net Ratio) are
            intentionally not duplicated on this page either — see the OI Flow Charts tab for those.
          </Typography>
          <Grid container spacing={0.5}>
            {NOT_YET_AVAILABLE.map((item) => (
              <Grid item xs={12} sm={6} key={item}>
                <Typography variant="caption" color="text.secondary">
                  • {item}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* ---- Latest Alerts ---- */}
        <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Latest Alerts
            </Typography>
            <Stack direction="row" spacing={1}>
              {(["ALL", "P1", "P2"] as const).map((p) => (
                <Chip
                  key={p}
                  size="small"
                  label={p === "ALL" ? "All" : p}
                  variant={alertPriorityFilter === p ? "filled" : "outlined"}
                  color={alertPriorityFilter === p ? (p === "P1" ? "error" : p === "P2" ? "warning" : "primary") : "default"}
                  onClick={() => setAlertPriorityFilter(p)}
                />
              ))}
            </Stack>
          </Stack>
          {alertsError && <Alert severity="warning">{alertsError}</Alert>}
          {alerts && alerts.events.length === 0 && !alertsError && (
            <Typography variant="body2" color="text.secondary">
              No alerts yet for {symbol}.
            </Typography>
          )}
          {alerts && alerts.events.length > 0 && (() => {
            const filteredEvents =
              alertPriorityFilter === "ALL"
                ? alerts.events
                : alerts.events.filter((e) => e.priority === alertPriorityFilter);
            return filteredEvents.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No {alertPriorityFilter} alerts for {symbol} right now.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEvents.map((event, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ fontFamily: MONO_FONT, whiteSpace: "nowrap" }}>
                          {new Date(event.occurred_at).toLocaleString("en-IN")}
                        </TableCell>
                        <TableCell>
                          {event.priority ? (
                            <Chip
                              size="small"
                              label={event.priority}
                              color={event.priority === "P1" ? "error" : "warning"}
                            />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={event.event_type} />
                        </TableCell>
                        <TableCell>{event.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}
        </Paper>
    </Box>
  );
}

export function OptionIndicatorPage() {
  const view = useAnalystIndexView();
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} {...indexSectionProps(view, "NIFTY", "lg")}>
        <OptionIndicatorSymbolPanel symbol="NIFTY" />
      </Grid>
      <Grid item xs={12} {...indexSectionProps(view, "BANKNIFTY", "lg")}>
        <OptionIndicatorSymbolPanel symbol="BANKNIFTY" />
      </Grid>
    </Grid>
  );
}

export default OptionIndicatorPage;
